import {
  buildOptionalParams,
  buildPath,
  buildTemplatePath,
  Card,
  CrudMode,
  Dialog,
  handleClose,
  Page,
  PageRoute,
  ScreenType,
  Typography,
  useParams,
  useUnsavedChanges,
  useValidationErrors,
} from "@compass/core";
import {
  Permission,
  StudentEnrollment,
  StudentEnrollmentEditResource,
  StudentEnrollmentViewResource,
} from "generated/openApi";
import "moment-timezone";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ApiService from "services/apiService";
import { StudentEnrollmentPageBody } from "./StudentEnrollmentPageBody";
import { StudentEnrollmentPageHeader } from "./StudentEnrollmentPageHeader";

const routeName = "studentEnrollment";

class Params {
  id: number = 0;
  crudMode: CrudMode = "view";
  screenType: ScreenType = "standard";
}

class OptionalParams {
  studentId?: number = 0;
  hideStudent?: boolean;
}

export const studentEnrollmentPageRoute: PageRoute<Params, OptionalParams> = {
  handle: "Enrollment",
  templatePath: buildTemplatePath(routeName, Params),
  getPath: (params) => buildPath(routeName, params),
  getOptionalParams: (params) => buildOptionalParams(params),
};

const ROOT_PERMISSION: Permission = Permission.StudentEnrollment;

export const StudentEnrollmentPage = () => {
  const { params, optionalParams } = useParams(routeName, Params, OptionalParams);
  const navigate = useNavigate();

const [hideStudent] = useState<boolean>(
  optionalParams.hideStudent ?? (params.screenType === "dialog")
    );

  const [studentId] = useState<number | undefined>(optionalParams.studentId);

  const [enrollment, setEnrollment] = useState<StudentEnrollment>({} as StudentEnrollment);
  const { unsavedChanges, resetUnsavedChanges } = useUnsavedChanges({ trackedItems: [enrollment] });
  const [viewResource, setViewResource] = useState<StudentEnrollmentViewResource>({});
  const [editResource, setEditResource] = useState<StudentEnrollmentEditResource>({});
  const { validationErrors, setValidationErrors } = useValidationErrors();

  useEffect(() => {
    fetch(params.id);
    fetchViewResource();
  }, []);

  useEffect(() => {
    if (params.crudMode === "edit") fetchEditResource();
  }, [params.crudMode]);

  const reroute = (id: number, crudMode: CrudMode) =>
    navigate(`../${studentEnrollmentPageRoute.getPath({ id: id, crudMode: crudMode, screenType: params.screenType })}`);

  const fetch = (id: number) => {
    ApiService.studentEnrollments.get(id).then((response) => {
      if (!id && studentId) response.studentId = studentId;

      setEnrollment(response);
      resetUnsavedChanges([response]);
    });
  };

  const fetchEditResource = () => {
    ApiService.studentEnrollments.getEditResource().then((response) => {
      setEditResource(response);
    });
  };

  const fetchViewResource = () => {
    ApiService.studentEnrollments.getViewResource().then((response) => {
      setViewResource(response);
    });
  };

  const pageHeader = (
    <StudentEnrollmentPageHeader
      rootPermission={ROOT_PERMISSION}
      handle={studentEnrollmentPageRoute.handle}
      enrollment={enrollment}
      reroute={reroute}
      fetch={fetch}
      crudMode={params.crudMode}
      setValidationErrors={setValidationErrors}
      unsavedChanges={unsavedChanges}
    />
  );

  const pageBody = (
    <StudentEnrollmentPageBody
      crudMode={params.crudMode}
      enrollment={enrollment}
      setEnrollment={setEnrollment}
      viewResource={viewResource}
      editResource={editResource}
      validationErrors={validationErrors}
      hideStudent={hideStudent}
    />
  );

  return params.screenType === "standard" ? (
    <Page isForm header={pageHeader}>
      <Card>{pageBody}</Card>
    </Page>
  ) : (
    <Dialog
      isForm
      open={true}
      onClose={() => handleClose(navigate, unsavedChanges)}
      maxWidth="md"
      header={pageHeader}
      content={pageBody}
    />
  );
};
